import React from 'react';

// Lightweight module-scoped tracker for assemblies currently updating
// Use functions below to mark/update state across app without introducing new stores

const assembliesUpdating = new Set<string>();
const listeners = new Set<() => void>();

// Notify all listeners when the updating state changes
const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

export const markAssemblyUpdating = (assemblyId: string | undefined | null) => {
  if (!assemblyId) return;
  const wasEmpty = assembliesUpdating.size === 0;
  assembliesUpdating.add(assemblyId);
  if (wasEmpty) {
    notifyListeners();
  }
};

export const markAssembliesUpdating = (assemblyIds: Array<string | undefined | null>) => {
  const wasEmpty = assembliesUpdating.size === 0;
  assemblyIds.forEach(id => id && assembliesUpdating.add(id));
  if (wasEmpty && assembliesUpdating.size > 0) {
    notifyListeners();
  }
};

export const markAssemblyUpdated = (assemblyId: string | undefined | null) => {
  if (!assemblyId) return;
  const hadAssembly = assembliesUpdating.has(assemblyId);
  assembliesUpdating.delete(assemblyId);
  if (hadAssembly) {
    notifyListeners();
  }
};

export const markAssembliesUpdated = (assemblyIds: Array<string | undefined | null>) => {
  let hadAny = false;
  assemblyIds.forEach(id => {
    if (id && assembliesUpdating.has(id)) {
      assembliesUpdating.delete(id);
      hadAny = true;
    }
  });
  if (hadAny) {
    notifyListeners();
  }
};

export const isAssemblyUpdating = (assemblyId: string | undefined | null): boolean => {
  if (!assemblyId) return false;
  return assembliesUpdating.has(assemblyId);
};

// Hook for React components to subscribe to updating state changes
export const useAssemblyUpdating = (assemblyId: string | undefined | null) => {
  const [isUpdating, setIsUpdating] = React.useState(() => isAssemblyUpdating(assemblyId));

  React.useEffect(() => {
    const listener = () => {
      setIsUpdating(isAssemblyUpdating(assemblyId));
    };

    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }, [assemblyId]);

  return isUpdating;
};
